import { NgClass } from '@angular/common';
import { Component, computed, inject, input, model, output } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ViewportService } from '@core/services/viewport.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { ProfileDateFilterComponent } from '@shared/components/profile-date-filter/profile-date-filter.component';
import {
  EDateRangeType,
  IDateRangeBounds,
  IProfileDateFilterState,
} from '@shared/components/profile-date-filter/profile-date-filter.interface';
import { ProfileGeneralFiltersComponent } from '@shared/components/profile-general-filters/profile-general-filters.component';
import { ProfilePlatformFeatureSelectComponent } from '@shared/components/profile-platform-feature-select/profile-platform-feature-select.component';
import { TPlatform } from '@shared/components/social-badge-list/social-badge-list.interface';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ICampaignAnalyticsFilter } from '../campaign-details.interface';

@Component({
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    NgClass,
    ReactiveFormsModule,
    TranslateModule,
    NgxSkeletonLoaderModule,
    ProfileDateFilterComponent,
    ProfileGeneralFiltersComponent,
    ProfilePlatformFeatureSelectComponent,
  ],
  selector: 'app-campaign-details-filters',
  templateUrl: './campaign-details-filters.component.html',
})
export class CampaignDetailsFiltersComponent {
  private readonly viewportService = inject(ViewportService);

  public readonly campaignAnalytics = input.required<ICampaignAnalytics | null>();
  public readonly campaignAnalyticsFilter = model.required<ICampaignAnalyticsFilter>();
  public readonly dateFilterState = input<IProfileDateFilterState | null>(null);
  public readonly allowedDateRange = input<IDateRangeBounds | null>(null);
  /** Whether the date filter's initial state/bounds are fully resolved and safe to mount. */
  public readonly dateFilterReady = input<boolean>(true);

  public readonly disabled = input<boolean>(false);
  public readonly loading = input<boolean>(false);
  public readonly limitReached = input<boolean>(false);
  public readonly creatorBriefingMode = input<boolean>(false);

  // On phones the platform select is narrower than its options ("Instagram Story" plus the checkbox), so the panel
  // sizes to its content there instead of matching the trigger.
  public readonly platformPanelWidth = computed(() =>
    this.creatorBriefingMode() && this.viewportService.isMobile() ? null : 'auto',
  );

  public readonly campaignAnalyticsFilterChanged = output<ICampaignAnalyticsFilter>();
  public readonly dateRangeChanged = output<Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'>>();
  public readonly selectedRangeChanged = output<EDateRangeType>();

  public onPlatformFeaturesChanged(platformFeatures: Set<TPlatform>): void {
    if (this.disabled() || this.limitReached()) {
      return;
    }

    this.campaignAnalyticsFilter.set({
      ...this.campaignAnalyticsFilter(),
      platformFeatures: new Set(Array.from(platformFeatures) as EPlatformFeature[]),
    });
  }

  public onCreatorIdsChanged(creatorIds: Set<string>): void {
    if (this.disabled() || this.limitReached()) {
      return;
    }

    this.campaignAnalyticsFilter.set({
      ...this.campaignAnalyticsFilter(),
      creatorIds: new Set(Array.from(creatorIds)),
    });
  }

  public onPlatformFeaturesPanelClosed(): void {
    if (this.disabled() || this.limitReached()) {
      return;
    }

    this.campaignAnalyticsFilterChanged.emit(this.campaignAnalyticsFilter());
  }

  public onCreatorIdsPanelClosed(): void {
    if (this.disabled() || this.limitReached()) {
      return;
    }

    this.campaignAnalyticsFilterChanged.emit(this.campaignAnalyticsFilter());
  }

  public onDateRangeChanged(dateRange: Pick<ICampaignAnalyticsFilter, 'endDate' | 'startDate'>): void {
    if (this.disabled() || this.limitReached()) {
      return;
    }

    this.campaignAnalyticsFilter.set({
      ...this.campaignAnalyticsFilter(),
      endDate: dateRange.endDate,
      startDate: dateRange.startDate,
    });

    this.dateRangeChanged.emit(dateRange);
    this.campaignAnalyticsFilterChanged.emit(this.campaignAnalyticsFilter());
  }

  public onSelectedRangeChanged(selectedRange: EDateRangeType): void {
    if (this.disabled() || this.limitReached()) {
      return;
    }

    this.selectedRangeChanged.emit(selectedRange);
  }
}
